import {
  MessageProps,
  MessageType,
  ChatInputMentionContent,
} from '@digivox/design-system-smartspace';

/**
 * Tipos de conteúdo de mensagem suportados
 */
export type MessageContentType = 'text' | 'file' | 'reply' | 'forward' | 'historic' | 'event';

/**
 * Tipos de anexo suportados
 */
export enum AttachmentType {
  IMAGE = 'image',
  DOC = 'doc',
  VIDEO = 'video',
  AUDIO = 'audio',
}

/**
 * Extensões de arquivo suportadas
 */
export enum AttachmentExtension {
  // Imagens
  GIF = 'gif',
  WEBP = 'webp',
  JPEG = 'jpeg',
  JPG = 'jpg',
  PNG = 'png',
  // Vídeos
  MP4 = 'mp4',
  WEBM = 'webm',
  // Áudios
  MP3 = 'mp3',
  WAV = 'wav',
  OGG = 'ogg',
  WAVE = 'wave',
  // Documentos
  PDF = 'pdf',
  DOC = 'doc',
  DOCX = 'docx',
  CSV = 'csv',
  XML = 'xml',
  XLS = 'xls',
  XLSX = 'xlsx',
  TXT = 'txt',
  HTML = 'html',
  XHTML = 'xhtml',
}

/**
 * Estrutura de anexo de mensagem
 */
export interface MessageAttachment {
  name: string;
  type: AttachmentType;
  extension: AttachmentExtension;
  size?: string;
  url: string;
  urlSmall?: string;
}

/**
 * Estrutura de resposta a mensagem
 */
export interface MessageReply {
  to: string;
  message: string;
  messageId: string;
  userName?: string;
  userId?: string;
  timestamp?: string;
  messageType?: string;
}

/**
 * Estrutura de mensagem encaminhada
 */
export interface MessageForward {
  username: string;
  datetime: string;
  originalId?: string;
  originalName?: string;
  userId?: string;
  messageType?: string;
  timestamp?: string;
  chainCount?: number;
  originalMessage?: string;
}

/**
 * Mensagem do chat de conferência
 */
export interface ConferenceChatMessage {
  id: string;
  idXmpp?: string;
  message: string;
  fromUser: string;
  toUser?: string;
  userName?: string;
  sentAt: string;
  time?: string;
  isOwn: boolean;
  contentType: MessageContentType;
  content?: string;
  // Campos de arquivo
  fileUrl?: string | null;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  // Flags de tipo de mídia
  isFileMessage?: boolean;
  isImageMessage?: boolean;
  isVideoMessage?: boolean;
  isAudioMessage?: boolean;
  // Relacionamentos
  reply?: MessageReply | null;
  forward?: MessageForward | null;
  // Status
  deliverStatus?: string;
  readStatus?: string;
}

/**
 * Props do componente de mensagens
 */
export interface ConferenceChatPaginationProps {
  hasOlderMessages?: boolean;
  isLoadingHistory?: boolean;
  onLoadOlderMessages?: () => Promise<void>;
}

export interface ConferenceChatMessagesProps extends ConferenceChatPaginationProps {
  messages: ConferenceChatMessage[];
  isLoading?: boolean;
}

/**
 * Props do painel de chat
 */
export interface ConferenceChatPanelProps extends ConferenceChatPaginationProps {
  isOpen: boolean;
  onClose: () => void;
  messages: ConferenceChatMessage[];
  onSendMessage: (message: string, metadata: ConferenceMessageMetadata) => void;
  isConnected: boolean;
  isConnecting: boolean;
  groupName?: string;
  error?: string | null;
  groupId?: string;
}

/**
 * Representação de uma menção produzida pelo campo de entrada.
 *
 * O rótulo é utilizado na interface, enquanto o ID identifica o destinatário
 * nos metadados enviados pelo XMPP.
 */
export interface ConferenceMessageMention {
  id: string;
  label: string;
  isAll?: boolean;
}

/**
 * Metadados enviados junto ao texto visível da mensagem.
 *
 * O documento estruturado preserva a posição das menções e a lista resumida
 * é utilizada para construir a extensão XMPP.
 */
export interface ConferenceMessageMetadata {
  mentions: ConferenceMessageMention[];
  content?: ChatInputMentionContent['json'];
}

/**
 * Props do input de chat
 */
export interface ConferenceChatInputProps {
  onSendMessage: (message: string, metadata: ConferenceMessageMetadata) => void;
  isConnected: boolean;
  placeholder?: string;
  onAttachFile?: (files: File[]) => void;
  isUploading?: boolean;
}

/**
 * Props do botão de chat
 */
export interface ConferenceChatButtonProps {
  onClick: () => void;
  unreadCount?: number;
  isConnected?: boolean;
}

/**
 * Re-export dos tipos do design system para conveniência
 */
export { MessageType };
export type { MessageProps };
